'use client'
import * as React from 'react'
import { createStore, useStore } from 'zustand'
import type { PendingMutation } from 'replicache'
import { useRepoReplicache } from '../../../../hooks-data/use-repo-replicache'
import { sleep } from '../../../../lib/sleep'
import { RepoSpaceMutators } from '@basehub/data/replicache/mutators/repo-space/mutators'

type RepoReplicacheSyncStateStore = {
  isSyncing: boolean
  syncsDone: number
  isOffline: boolean
  isSaving: boolean
  pendingMutations?: readonly PendingMutation[]
  setIsSyncing: (isSyncing: boolean) => void
  incrementSyncsDone: () => void
  setIsOffline: (isOffline: boolean) => void
  setIsSaving: (isSaving: boolean) => void
  setPendingMutations: (pendingMutations: readonly PendingMutation[]) => void
}

const createRepoReplicacheSyncStateStore = () =>
  createStore<RepoReplicacheSyncStateStore>()((set) => ({
    isSyncing: true,
    syncsDone: 0,
    isOffline: false,
    isSaving: false,
    pendingMutations: undefined,
    setIsSyncing: (isSyncing) => set({ isSyncing }),
    incrementSyncsDone: () =>
      set((state) => ({ syncsDone: state.syncsDone + 1 })),
    setIsOffline: (isOffline) => set({ isOffline }),
    setIsSaving: (isSaving) => set({ isSaving }),
    setPendingMutations: (pendingMutations) => set({ pendingMutations }),
  }))

const RepoReplicacheSyncStateContext = React.createContext<ReturnType<
  typeof createRepoReplicacheSyncStateStore
> | null>(null)

export const useRepoReplicacheSyncStateStore = <T,>(
  selector: (state: RepoReplicacheSyncStateStore) => T
): T => {
  const store = React.useContext(RepoReplicacheSyncStateContext)
  if (!store) {
    throw new Error(
      'Missing RepoReplicacheSyncStateContext.Provider in the tree'
    )
  }
  return useStore(store, selector)
}

export const RepoReplicacheSyncStateProvider = ({
  children,
}: {
  children?: React.ReactNode
}) => {
  return <InnerContext>{children}</InnerContext>
}

const InnerContext = ({ children }: { children?: React.ReactNode }) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache

  const store = React.useMemo(() => createRepoReplicacheSyncStateStore(), [])
  const isSyncing = useStore(store, (state) => state.isSyncing)
  const isSaving = useStore(store, (state) => state.isSaving)
  const setIsSyncing = useStore(store, (state) => state.setIsSyncing)
  const incrementSyncsDone = useStore(
    store,
    (state) => state.incrementSyncsDone
  )
  const setIsOffline = useStore(store, (state) => state.setIsOffline)
  const setIsSaving = useStore(store, (state) => state.setIsSaving)
  const setPendingMutations = useStore(
    store,
    (state) => state.setPendingMutations
  )

  const isSyncingRef = React.useRef(isSyncing) // to use without needing to re-render

  React.useEffect(() => {
    if (!replicache) return
    // eslint-disable-next-line react-compiler/react-compiler
    replicache.onSync = (syncing) => {
      if (!syncing && isSyncingRef.current) {
        // ended sync
        incrementSyncsDone()
      }
      setIsSyncing(syncing)
      isSyncingRef.current = syncing
    }

    replicache.onOnlineChange = (online) => {
      setIsOffline(!online)
    }
  }, [replicache, setIsSyncing, incrementSyncsDone, setIsOffline])

  // pending mutations
  React.useEffect(() => {
    if (!replicache) return

    let interval = true

    if (isSyncing) {
      const checkExperimentalPendingMutations = async () => {
        if (!interval) {
          return // stop interval
        }
        const pendingMutations = await replicache.experimentalPendingMutations()
        setPendingMutations(pendingMutations)

        const ignoredMutations: Array<keyof RepoSpaceMutators> = [
          'initUserPreferences',
          'toggleCollapsePropertiesPanel',
          'setCollapsedDocumentPanel',
          'setCollapsedPropertiesPanel',
          'initClient',
          'updateClientFocus',
          'listActiveClients',
          'keepClientAlive',
          'setEditMode',
          'createThread',
          'setSelectedListView',
          'toggleSelectedVariant',
          '__deduped' as 'updateBlock',
          'setCommentRead',
          'setSelectedThreadByBlock',
          'resolveComment',
          'setSelectedVariants',
          'toggleSelectedVariant',
          'setPropertiesPanelMode',
          'setCommitsFilters',
          'setCommentRead',
          'setCollapsedState',
        ]

        setIsSaving(
          pendingMutations.some(
            (m) =>
              ignoredMutations.includes(m.name) === false &&
              m.clientID === data?.clientId
          )
        )
        await sleep(200)
        checkExperimentalPendingMutations()
      }

      checkExperimentalPendingMutations()

      return () => {
        interval = false
      }
    } else {
      interval = false

      const timeout = setTimeout(() => {
        setIsSaving(false)
      }, 1000)

      return () => {
        interval = false
        clearTimeout(timeout)
      }
    }
    // include isSyncing so that we re-run this checker.
  }, [replicache, isSyncing, data?.clientId, setPendingMutations, setIsSaving])

  // handle onbeforeunload
  React.useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isSaving) {
        event.preventDefault()
        event.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isSaving])

  return (
    <RepoReplicacheSyncStateContext.Provider value={store}>
      {children}
    </RepoReplicacheSyncStateContext.Provider>
  )
}
